import { useState, useRef, useCallback } from 'react';
import { useMutation } from 'convex/react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../convex/_generated/api';
import './PdfImportModal.css';

const MAX_PAGES = 500;

let pdfModulePromise = null;
const loadPdfModule = async () => {
  if (!pdfModulePromise) {
    pdfModulePromise = import('pdfjs-dist/legacy/build/pdf.mjs').then(async (pdfjs) => {
      const worker = await import('pdfjs-dist/legacy/build/pdf.worker.mjs?url');
      pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
      return pdfjs;
    });
  }
  return pdfModulePromise;
};

// ── Smart chapter detection: tries multiple strategies ──

// Strategy 1: Explicit chapter keywords in many languages
const CHAPTER_REGEX = /^(?:chapter|ch\.?|part|פרק|חלק|chapitre|capítulo|capitulo|kapitel|capítol|глава|часть|الفصل|الجزء|capitolo|hoofdstuk|bölüm)\s+[\divxlc\u0660-\u0669\u05D0-\u05EA]+[.:)\s\-–]*/i;

// Strategy 2: Standalone number or Hebrew letter at start of page (likely a chapter number)
const STANDALONE_NUMBER_REGEX = /^[\u05D0-\u05EA]'?\.?$|^\d{1,3}\.?$/;

// Strategy 3: Short title-like line followed by much longer text (scene/chapter break)
const isTitleLine = (line) => {
  const trimmed = line.trim();
  return trimmed.length > 0 && trimmed.length < 60 && !/[.,:;!?]$/.test(trimmed);
};

// Strategy 4: Common separator patterns
const SEPARATOR_REGEX = /^[\s*\-_=~]{3,}$/;

function splitIntoChapters(pages) {
  // Strategy 1: Try explicit chapter keywords first
  let chapters = trySplitByRegex(pages, CHAPTER_REGEX);
  if (chapters.length > 1) return chapters;

  // Strategy 2: Try standalone numbers/letters at page starts
  chapters = trySplitByRegex(pages, STANDALONE_NUMBER_REGEX);
  if (chapters.length > 1) return chapters;

  // Strategy 3: Look for separator patterns + short title lines within page text
  chapters = trySplitBySeparators(pages);
  if (chapters.length > 1) return chapters;

  // Strategy 4: For large docs with no detectable chapters, split by page groups
  const totalText = pages.join(' ');
  const wordCount = totalText.split(/\s+/).length;
  if (wordCount > 5000 && pages.length > 10) {
    return splitByPageGroups(pages);
  }

  return [{ title: 'Chapter 1', pages }];
}

function trySplitByRegex(pages, regex) {
  const chapters = [];
  let current = { title: 'Chapter 1', pages: [] };

  for (let i = 0; i < pages.length; i++) {
    const text = pages[i];
    const lines = text.split('\n').filter((l) => l.trim());
    const firstLine = lines[0]?.trim() || '';

    if (i > 0 && regex.test(firstLine)) {
      if (current.pages.length > 0) chapters.push(current);
      const title = firstLine.replace(/[.:)\s\-–]+$/, '').trim();
      current = { title: title || `Chapter ${chapters.length + 2}`, pages: [] };
    }
    current.pages.push(text);
  }

  if (current.pages.length > 0) chapters.push(current);
  return chapters;
}

function trySplitBySeparators(pages) {
  // Combine all pages and look for separator patterns (*** or ---) within text
  const fullText = pages.join('\n\n---PAGE---\n\n');
  const lines = fullText.split('\n');
  const breaks = [];

  for (let i = 0; i < lines.length; i++) {
    if (SEPARATOR_REGEX.test(lines[i].trim())) {
      // Look for a title line after the separator
      let titleIdx = i + 1;
      while (titleIdx < lines.length && !lines[titleIdx].trim()) titleIdx++;
      if (titleIdx < lines.length && isTitleLine(lines[titleIdx])) {
        breaks.push({ lineIdx: i, title: lines[titleIdx].trim() });
      }
    }
  }

  if (breaks.length < 2) return []; // Not enough breaks found

  // Rebuild chapters from break points
  const chapters = [];
  const allLines = lines;
  let startLine = 0;

  for (const brk of breaks) {
    const chapterLines = allLines.slice(startLine, brk.lineIdx).join('\n');
    if (chapterLines.trim()) {
      chapters.push({
        title: chapters.length === 0 ? 'Chapter 1' : breaks[chapters.length - 1]?.title || `Chapter ${chapters.length}`,
        pages: [chapterLines.replace(/---PAGE---/g, '\n\n')],
      });
    }
    startLine = brk.lineIdx + 1;
  }

  // Remaining text
  const remaining = allLines.slice(startLine).join('\n');
  if (remaining.trim()) {
    chapters.push({
      title: breaks[breaks.length - 1]?.title || `Chapter ${chapters.length + 1}`,
      pages: [remaining.replace(/---PAGE---/g, '\n\n')],
    });
  }

  return chapters;
}

function splitByPageGroups(pages) {
  // Split every ~15 pages as a chapter (reasonable chapter length)
  const PAGES_PER_CHAPTER = 15;
  const chapters = [];

  for (let i = 0; i < pages.length; i += PAGES_PER_CHAPTER) {
    const chapterPages = pages.slice(i, i + PAGES_PER_CHAPTER);
    // Try to use first meaningful line as title
    const firstLine = chapterPages[0]?.split('\n').find((l) => l.trim())?.trim() || '';
    const title = firstLine.length > 3 && firstLine.length < 80
      ? firstLine.slice(0, 60)
      : `Chapter ${chapters.length + 1}`;

    chapters.push({ title, pages: chapterPages });
  }

  return chapters;
}

function PdfImportModal({ projectId, onClose, onSuccess }) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [stage, setStage] = useState('upload'); // upload | preview | importing
  const [fileName, setFileName] = useState('');
  const [chapters, setChapters] = useState([]);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');

  const createItem = useMutation(api.files.createItem);

  const handleFileSelect = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file || file.type !== 'application/pdf') {
      setError(t('pdfImport.invalidFile'));
      return;
    }

    setFileName(file.name);
    setError('');
    setProgress(t('pdfImport.extracting'));

    try {
      const pdfjs = await loadPdfModule();
      const buffer = await file.arrayBuffer();
      const pdf = await pdfjs.getDocument({ data: buffer }).promise;
      const totalPages = Math.min(pdf.numPages, MAX_PAGES);
      const pages = [];

      for (let i = 1; i <= totalPages; i++) {
        setProgress(t('pdfImport.readingPage', { current: i, total: totalPages }));
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent({ disableStream: true });
        const pageText = textContent.items
          .map((item) => ('str' in item ? item.str : ''))
          .join(' ')
          .replace(/\s{3,}/g, '\n\n')
          .replace(/\s+/g, ' ')
          .trim();
        if (pageText) pages.push(pageText);
      }

      if (pages.length === 0) {
        setError(t('pdfImport.couldNotExtract'));
        setStage('upload');
        return;
      }

      const detectedChapters = splitIntoChapters(pages);
      setChapters(detectedChapters.map((ch, i) => ({
        id: i,
        title: ch.title,
        content: ch.pages.join('\n\n'),
        include: true,
      })));
      setStage('preview');
      setProgress('');
    } catch (err) {
      setError(err.message || t('pdfImport.failedToRead'));
      setStage('upload');
    }
  }, [t]);

  const handleTitleChange = (id, newTitle) => {
    setChapters((prev) => prev.map((ch) => ch.id === id ? { ...ch, title: newTitle } : ch));
  };

  const handleToggleInclude = (id) => {
    setChapters((prev) => prev.map((ch) => ch.id === id ? { ...ch, include: !ch.include } : ch));
  };

  const handleImport = async () => {
    const toImport = chapters.filter((ch) => ch.include);
    if (toImport.length === 0) return;

    setStage('importing');
    setError('');

    try {
      for (let i = 0; i < toImport.length; i++) {
        const ch = toImport[i];
        setProgress(t('pdfImport.importingProgress', { current: i + 1, total: toImport.length, title: ch.title }));
        const safeName = ch.title.replace(/[\/\\:*?"<>|]/g, '').trim().replace(/\s+/g, '-');
        const name = `${safeName || `chapter-${i + 1}`}.md`;
        const path = `Chapters/${name}`;
        const content = `# ${ch.title}\n\n${ch.content}`;

        await createItem({
          projectId,
          path,
          name,
          parentPath: 'Chapters',
          type: 'file',
          content,
        });
      }

      onSuccess?.(t('pdfImport.importedSuccess', { count: toImport.length }));
      onClose();
    } catch (err) {
      setError(err.message || t('pdfImport.failedToImport'));
      setStage('preview');
    }
  };

  const selectedCount = chapters.filter((ch) => ch.include).length;
  const totalWords = chapters.filter((ch) => ch.include).reduce((sum, ch) => sum + ch.content.split(/\s+/).length, 0);

  return (
    <div className="pdf-import-overlay" onClick={onClose}>
      <div className="pdf-import-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pdf-import-header">
          <h2>{t('pdfImport.title')}</h2>
          <button className="pdf-import-close" onClick={onClose}>&times;</button>
        </div>

        {error && <div className="pdf-import-error">{error}</div>}
        {progress && <div className="pdf-import-progress">{progress}</div>}

        {stage === 'upload' && (
          <div className="pdf-import-upload">
            <p>{t('pdfImport.uploadPrompt')}</p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,application/pdf"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            <button className="pdf-import-btn" onClick={() => fileInputRef.current?.click()}>
              {t('pdfImport.chooseFile')}
            </button>
          </div>
        )}

        {stage === 'preview' && (
          <div className="pdf-import-preview">
            <div className="pdf-import-summary">
              <span>{fileName}</span>
              <span>{t('pdfImport.chaptersSelected', { count: selectedCount })}</span>
              <span>{totalWords.toLocaleString()} {t('common.words')}</span>
            </div>

            <div className="pdf-import-chapters">
              {chapters.map((ch) => (
                <div key={ch.id} className={`pdf-import-chapter ${ch.include ? '' : 'excluded'}`}>
                  <label className="pdf-import-check">
                    <input type="checkbox" checked={ch.include} onChange={() => handleToggleInclude(ch.id)} />
                  </label>
                  <input
                    className="pdf-import-title-input"
                    value={ch.title}
                    onChange={(e) => handleTitleChange(ch.id, e.target.value)}
                  />
                  <span className="pdf-import-wordcount">
                    {ch.content.split(/\s+/).length.toLocaleString()} words
                  </span>
                  <p className="pdf-import-excerpt">{ch.content.slice(0, 200)}...</p>
                </div>
              ))}
            </div>

            <div className="pdf-import-actions">
              <button className="btn btn-secondary" onClick={() => { setStage('upload'); setChapters([]); }}>
                {t('common.back')}
              </button>
              <button className="btn btn-primary" onClick={handleImport} disabled={selectedCount === 0}>
                {t('pdfImport.importButton', { count: selectedCount })}
              </button>
            </div>
          </div>
        )}

        {stage === 'importing' && (
          <div className="pdf-import-importing">
            <div className="pdf-import-spinner" />
            <p>{progress}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default PdfImportModal;
